import { useIsMutating } from "@tanstack/react-query";
import clsx from "clsx";
import { MessageSquarePlus, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useGenesis } from "../app/dossierGenesis";
import { designateSoon } from "../components/attention";
import { Motion, Presence } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { useDismiss } from "../components/useDismiss";
import { onboardingSeen, useHelp } from "../help/helpStore";
import { t } from "../i18n/i18n";
import { useBriefingJob } from "../views/briefing/briefingJob";
import { useCountryJob } from "../views/country/countryJob";
import { launcherSelector, setFeedbackMenu, useFeedback, visibleFeedbackPlace } from "./feedbackModel";

/** Annonce du bouton « Retours » (registre F39) : une seule fois par poste, une bulle douce pointe le bouton visible
 *  (accueil ou barre latérale) — « Nouveau : proposez une évolution ou signalez une anomalie ». Elle attend que rien
 *  d'autre ne demande l'attention : prise en main vue et fermée, aucune aide, fenêtre ni menu ouverts, aucune
 *  génération ni enregistrement en cours, bouton à l'écran ; puis apparaît en fondu, le bouton pulse. Fermée (croix,
 *  « Compris », ou appui sur le bouton), elle ne revient plus : le poste s'en souvient (stockage local, protégé ;
 *  sans stockage, elle ne s'affiche qu'une fois par chargement). Jamais dans une capture (`data-capture-skip`). */

export const ANNOUNCE_KEY = "generex:retours-annonce";
/** Relevé des conditions, et nombre de relevés favorables d'affilée avant d'apparaître (environ 2 s de calme). */
const TICK_MS = 700;
const CALM_TICKS = 3;

let announcedThisLoad = false;

export function announceSeen(): boolean {
  if (announcedThisLoad) return true;
  try {
    return localStorage.getItem(ANNOUNCE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markAnnounceSeen(): void {
  announcedThisLoad = true;
  try {
    localStorage.setItem(ANNOUNCE_KEY, "1");
  } catch {
    // Stockage indisponible (navigation privée) : l'annonce ne revient pas avant le prochain chargement.
  }
}

/** Remise à zéro pour les essais. */
export function resetAnnounceForTests(): void {
  announcedThisLoad = false;
}

export interface AnnounceSignals {
  seen: boolean;
  onboardingSeen: boolean;
  onboardingOpen: boolean;
  helpOpen: boolean;
  feedbackOpen: boolean;
  /** Fenêtre, menu ou voile ouvert dans la page (`[role=dialog]`, `[role=menu]`). */
  overlay: boolean;
  /** Génération ou enregistrement en cours (rédaction IA, genèse du dossier, requête d'écriture, `aria-busy`). */
  busy: boolean;
  /** Bouton « Retours » visible à l'écran. */
  targetVisible: boolean;
}

/** L'annonce peut-elle apparaître (fonction pure, essayée seule) ? */
export function announceAllowed(s: AnnounceSignals): boolean {
  return (
    !s.seen && s.onboardingSeen && !s.onboardingOpen && !s.helpOpen && !s.feedbackOpen && !s.overlay && !s.busy && s.targetVisible
  );
}

/** Bouton « Retours » visible (celui de l'accueil ou de la barre latérale), ou null. */
function launcher(): HTMLElement | null {
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(launcherSelector(visibleFeedbackPlace())))) {
    if (el.closest('[aria-hidden="true"]')) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.bottom <= window.innerHeight && r.right <= window.innerWidth) return el;
  }
  return null;
}

/** Place de la bulle : à droite du bouton de la barre latérale, sous celui de l'accueil (en-tête à droite). */
export function bubbleSpot(
  rect: { left: number; top: number; right: number; bottom: number; height: number },
  bubble: { width: number; height: number },
  viewport: { width: number; height: number },
): { side: "right" | "bottom"; left: number; top: number; arrow: number } {
  const gap = 10;
  const margin = 12;
  if (rect.left < viewport.width / 2) {
    const ideal = rect.top + rect.height / 2 - bubble.height / 2;
    const top = Math.max(margin, Math.min(ideal, viewport.height - bubble.height - margin));
    return { side: "right", left: rect.right + gap, top, arrow: rect.top + rect.height / 2 - top };
  }
  const left = Math.max(margin, Math.min(rect.right - bubble.width, viewport.width - bubble.width - margin));
  const center = (rect.left + rect.right) / 2;
  return { side: "bottom", left, top: rect.bottom + gap, arrow: center - left };
}

export function FeedbackAnnounce() {
  const [state, setState] = useState<"waiting" | "shown" | "done">(() => (announceSeen() ? "done" : "waiting"));
  const onboardingOpen = useHelp((s) => s.onboarding);
  const helpOpen = useHelp((s) => s.open);
  const feedbackOpen = useFeedback((s) => s.pane !== null || s.menu !== null);
  const briefing = useBriefingJob((s) => Boolean(s.job?.running));
  const country = useCountryJob((s) => Boolean(s.job?.running));
  const genesis = useGenesis((s) => s.genesis !== null);
  const mutating = useIsMutating() > 0;
  const signals = useRef({ onboardingOpen, helpOpen, feedbackOpen, busy: false });
  signals.current = { onboardingOpen, helpOpen, feedbackOpen, busy: briefing || country || genesis || mutating };
  const [paused, setPaused] = useState(false);

  // Attente du calme, relevée à intervalle : l'annonce n'interrompt jamais un travail en cours.
  useEffect(() => {
    if (state === "done") return;
    let calm = 0;
    const timer = window.setInterval(() => {
      const s = signals.current;
      const allowed = announceAllowed({
        seen: state === "waiting" && announceSeen(),
        onboardingSeen: onboardingSeen(),
        onboardingOpen: s.onboardingOpen,
        helpOpen: s.helpOpen,
        feedbackOpen: s.feedbackOpen,
        // L'accueil est lui-même une fenêtre (`data-welcome`) : seules comptent celles posées dessus ou sur l'espace
        // de travail.
        // L'animation d'ouverture (`.intro-overlay`) compte aussi : l'annonce attend sa fin (26/10/2026).
        overlay: Boolean(
          document.querySelector('[role="dialog"]:not([data-welcome]), [role="menu"], [role="alertdialog"], .intro-overlay'),
        ),
        busy: s.busy || Boolean(document.querySelector('[aria-busy="true"]')),
        targetVisible: launcher() !== null,
      });
      if (state === "waiting") {
        calm = allowed ? calm + 1 : 0;
        if (calm >= CALM_TICKS) {
          markAnnounceSeen();
          setState("shown");
        }
      } else {
        // Affichée : s'efface le temps d'une fenêtre, d'une aide ou d'une génération, puis revient.
        setPaused(!allowed);
      }
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [state]);

  // Appui sur le bouton « Retours » (menu ouvert) ou fenêtre d'un retour : l'annonce a servi.
  useEffect(() => {
    if (state === "shown" && feedbackOpen) setState("done");
  }, [state, feedbackOpen]);

  // Le bouton pulse à l'apparition (action désignée).
  useEffect(() => {
    if (state !== "shown") return;
    return designateSoon(launcherSelector(visibleFeedbackPlace()), "ring", 450);
  }, [state]);

  const dismiss = useCallback(() => setState("done"), []);
  const show = state === "shown" && !paused;
  return createPortal(
    <Presence show={show}>{show && <Bubble onClose={dismiss} />}</Presence>,
    document.body,
  );
}

function Bubble({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState<ReturnType<typeof bubbleSpot> | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const target = launcher();
      const el = ref.current;
      if (!target || !el) return;
      setSpot(bubbleSpot(target.getBoundingClientRect(), { width: el.offsetWidth, height: el.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }));
    };
    place();
    const timer = window.setInterval(place, 500);
    window.addEventListener("resize", place);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", place);
    };
  }, []);

  // Échap la ferme, par la pile partagée (sans clic dehors : la bulle ne bloque rien).
  useDismiss(true, onClose);

  const tryIt = () => {
    onClose();
    setFeedbackMenu(visibleFeedbackPlace());
  };

  return (
    <div
      ref={ref}
      data-capture-skip=""
      data-feedback-announce=""
      role="status"
      className={clsx("fixed z-[62] w-[300px] max-w-[calc(100vw-24px)]", !spot && "invisible")}
      style={spot ? { left: spot.left, top: spot.top } : { left: 0, top: 0 }}
    >
      <Motion origin={spot?.side === "bottom" ? "top" : "left"} className="relative rounded-xl border border-line bg-surface py-3 pr-2 pl-3.5 shadow-float">
        {/* Flèche vers le bouton : carré tourné, mêmes bordure et fond que la bulle. */}
        {spot && (
          <span
            aria-hidden
            className={clsx(
              "absolute size-2.5 rotate-45 border-line bg-surface",
              spot.side === "right" ? "-left-[6px] border-b border-l" : "-top-[6px] border-t border-l",
            )}
            style={spot.side === "right" ? { top: Math.max(12, Math.min(spot.arrow - 5, 200)) } : { left: Math.max(12, spot.arrow - 5) }}
          />
        )}
        <div className="flex items-start gap-2.5">
          <MessageSquarePlus size={16} className="mt-0.5 shrink-0 text-accent" aria-hidden />
          <div className="min-w-0 flex-1 py-px">
            <p className="text-ui leading-snug font-semibold text-ink">{t("Nouveau : proposez une évolution ou signalez une anomalie")}</p>
            <p className="mt-1 text-meta leading-snug text-ink-soft">
              {t("Le bouton « Retours » transmet vos idées et les défauts constatés à l'équipe Generex, qui vous répond dans « Mes retours ».")}
            </p>
            <div className="mt-2.5 flex items-center gap-2">
              <Button size="sm" onClick={tryIt}>
                {t("Essayer")}
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose}>
                {t("Compris")}
              </Button>
            </div>
          </div>
          <IconButton size="xs" onClick={onClose} title={t("Fermer")} aria-label={t("Fermer")} className="-mt-1">
            <X size={14} />
          </IconButton>
        </div>
      </Motion>
    </div>
  );
}
